import { useQuery } from '@tanstack/react-query'
import {
  DashboardAlphaReportStatusEnum,
  PostDashboardAlphaReportStatusesResponse,
} from '@ulysses-inc/harami_api_client'
import { demoDashboardAlphaApi } from 'src/features/monitoringDashboardAlpha/demo/demoDashboardAlphaApi'
import { queryKeys } from 'src/features/monitoringDashboardAlpha/detail/api/queryKeys'
import { DetailFilters } from 'src/features/monitoringDashboardAlpha/detail/hooks/useDetailFilters'
import {
  DashboardReportDetail,
  DashboardReportDetails,
} from 'src/features/monitoringDashboardAlpha/detail/types'
import { getDefaultDateRange } from 'src/features/monitoringDashboardAlpha/hooks/useMonitoringFilters'
import {
  ReportStatusGroupKey,
  ReportStatusKey,
} from 'src/features/monitoringDashboardAlpha/types'
import { stripTimeInfo } from 'src/util/datetime/stripTimeInfo'

/** API のステータス enum → フロントのステータスキー（notScheduled は L2 では扱わない） */
const toReportStatusKey: {
  [key in DashboardAlphaReportStatusEnum]: ReportStatusKey | null
} = {
  [DashboardAlphaReportStatusEnum.Implemented]: 'implemented',
  [DashboardAlphaReportStatusEnum.ImplementedAfterDeadline]:
    'implementedAfterDeadline',
  [DashboardAlphaReportStatusEnum.Recording]: 'recording',
  [DashboardAlphaReportStatusEnum.RecordingOverdue]: 'recordingOverdue',
  [DashboardAlphaReportStatusEnum.NotImplementedYet]: 'notImplementedYet',
  [DashboardAlphaReportStatusEnum.DuringPeriod]: 'duringPeriod',
  [DashboardAlphaReportStatusEnum.NotStarted]: 'notStarted',
  [DashboardAlphaReportStatusEnum.NotScheduled]: null,
}

/**
 * フロントの表示用ステータスグループ → API のステータス enum 配列（絞り込み送信用）
 *
 * L1 のカード／セルは細かいステータスをグループに束ねて表示しているため、
 * L2 ではグループに対応する複数ステータスで絞り込む。
 */
const toApiStatuses: {
  [key in ReportStatusGroupKey]: DashboardAlphaReportStatusEnum[]
} = {
  submitted: [
    DashboardAlphaReportStatusEnum.Implemented,
    DashboardAlphaReportStatusEnum.ImplementedAfterDeadline,
  ],
  notImplemented: [
    DashboardAlphaReportStatusEnum.NotImplementedYet,
    DashboardAlphaReportStatusEnum.RecordingOverdue,
  ],
  duringPeriod: [
    DashboardAlphaReportStatusEnum.DuringPeriod,
    DashboardAlphaReportStatusEnum.Recording,
  ],
  notStarted: [DashboardAlphaReportStatusEnum.NotStarted],
}

const adaptResponse = (
  raw: PostDashboardAlphaReportStatusesResponse,
): DashboardReportDetails => ({
  details: raw.reportStatuses
    .map((row, index): DashboardReportDetail | null => {
      const status = toReportStatusKey[row.status]
      if (!status) return null
      return {
        id: `${row.placeNodeUUID}-${row.scheduleUUID}-${row.date}-${index}`,
        scheduleUuid: row.scheduleUUID,
        scheduleName: row.scheduleName,
        placeNodeUuid: row.placeNodeUUID,
        placeName: row.placeName,
        date: row.date,
        status,
        reportUuid: row.reportUUID,
      }
    })
    .filter((detail): detail is DashboardReportDetail => detail !== null),
  count: raw.count,
  scheduleOptions: raw.scheduleOptions.map(option => ({
    uuid: option.scheduleUUID,
    name: option.scheduleName,
  })),
})

/**
 * モニタリングダッシュボード詳細（L2）の明細データを取得する
 *
 * セールスデモ（HRMSHI-250）の間は API（POST /dashboardAlpha/reportStatuses, harami_api_server #4835）を呼ばず、
 * demo/ で生成したダミーデータを返す。リクエストの組み立てと adaptResponse は API 呼び出し時と同じ。
 */
export const useQueryDashboardReportDetails = (filters: DetailFilters) => {
  return useQuery<DashboardReportDetails>({
    queryFn: async () => {
      // 期間が指定されていればその期間を、指定されていなければ既定期間を取得対象とする
      const [from, through] = filters.dateRange ?? getDefaultDateRange()
      const startDate = stripTimeInfo(new Date(from))
      const endDate = stripTimeInfo(new Date(through))

      const rawResponse =
        await demoDashboardAlphaApi.postDashboardAlphaReportStatuses({
          startDate,
          endDate,
          placeNodeIds:
            filters.placeNodeUuids.length > 0
              ? filters.placeNodeUuids
              : undefined,
          scheduleIds:
            filters.scheduleUuids.length > 0
              ? filters.scheduleUuids
              : undefined,
          statuses: filters.status ? toApiStatuses[filters.status] : undefined,
          offset: filters.offset,
          limit: filters.limit,
        })

      return adaptResponse(rawResponse)
    },
    queryKey: queryKeys.dashboardReportDetails(filters),
  })
}
